'use client';

import { ReactNode } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Controller } from 'react-hook-form';
import { resolveErrorMessage } from '@/shared/helpers/resolve-error-message';
import { useCreateAccount } from '@/features/auth/internal/hooks/use-create-account';
import { Form } from '@/shared/components/form/form';
import { InputField } from '@/shared/components/form/input-field';
import { MessageBox } from '@/shared/components/message-box';
import { FormSubmit } from '@/shared/components/form/form-submit';
import { Checkbox } from '@/shared/components/primitives/checkbox';
import { Field } from '@/shared/components/form/field';
import { pagesRoutes } from '@/shared/constants/routes';
import { SOCIAL_AUTH_PROVIDER_PARAM } from '@/shared/constants/search-params';
import { useSearchParams } from 'next/navigation';
import { GoogleLink } from '@/features/auth/shared/components/google-link';
import { Button } from '@/shared/components/primitives/button';
import { getProviderDisplayName } from '@/features/auth/shared/helpers/get-provider-display-name';
import { AccountProvider } from '@ecom/storefront-auth-db';
import {
  SOCIAL_LOGIN_ERRORS,
  SOCIAL_LOGIN_ERROR_PARAM,
} from '@/shared/types/errors/social-auth-errors';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { useAuthRedirect } from '@/features/auth/shared/hooks/use-auth-redirect';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';
import { getPasswordRequirementStatus } from '@/features/auth/shared/helpers/get-password-validation-errors';
import { FacebookLink } from '@/features/auth/shared/components/facebook-link';
import { FormSeparator } from '@/features/auth/internal/components/form-separator';

type CreateAccountFormProps = {
  titleId: string;
  newsletterLabel?: ReactNode;
  campaignId?: string | null;
  showSignInLink?: boolean;
  redirectUrl?: string;
  hideSocialButtons?: boolean;
  defaultEmail?: string;
  onSuccess?: (email: string) => void;
  onSwitchToLogin?: () => void;
  /** In-client social auth finishes in place and calls this instead of navigating. */
  onInClientSuccess?: () => void;
  /** Fired when the user commits to a social provider, before leaving the page. */
  onSocialStart?: (provider: AccountProvider) => void;
};

const socialLoginErrorValues = Object.values(SOCIAL_LOGIN_ERRORS) as string[];

export const CreateAccountForm = ({
  titleId,
  newsletterLabel,
  campaignId = null,
  showSignInLink = true,
  redirectUrl: redirectUrlProp,
  hideSocialButtons = false,
  defaultEmail = '',
  onSuccess,
  onSwitchToLogin,
  onInClientSuccess,
  onSocialStart,
}: CreateAccountFormProps) => {
  const searchParams = useSearchParams();
  const { redirectUrl, navigateWithCallback } = useAuthRedirect(redirectUrlProp);
  const campaignWelcomeHref = campaignId
    ? `/get-started/${encodeURIComponent(campaignId)}/welcome`
    : null;
  const callbackUrl = redirectUrl || campaignWelcomeHref || pagesRoutes.welcome.href;
  const t = useTranslations('Auth.createAccount');
  const isGamesApp = useIsWTGamesApp();

  const socialLoginError = searchParams.get(SOCIAL_LOGIN_ERROR_PARAM);
  const isValidSocialError = socialLoginError && socialLoginErrorValues.includes(socialLoginError);
  const {
    register,
    control,
    handleSubmit,
    errors,
    isSubmitting,
    onSubmit,
    subscribeToNewsletter,
    password,
  } = useCreateAccount({
    campaignId,
    defaultEmail,
    onSuccess,
    returnTo: onSuccess ? redirectUrl || undefined : undefined,
  });

  const passwordRequirements = getPasswordRequirementStatus(password ?? '');
  const showPasswordRequirementHints = Boolean(password?.length);
  const passwordRequirementItems = [
    {
      key: 'minLength',
      met: passwordRequirements.minLength,
      label: t('errors.passwordTooShort'),
    },
    {
      key: 'lettersNumbers',
      met: passwordRequirements.hasLetterAndNumber,
      label: t('errors.passwordMissingNumberOrLetter'),
    },
    {
      key: 'specialChar',
      met: passwordRequirements.hasSpecialChar,
      label: t('errors.passwordMissingSpecialChar'),
    },
  ];

  const handleCreateAccountClick = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin();
      return;
    }
    navigateWithCallback(pagesRoutes.login.href);
  };

  const displayNameFacebook = getProviderDisplayName(AccountProvider.FACEBOOK);
  const displayNameGoogle = getProviderDisplayName(AccountProvider.GOOGLE);

  const social_provider = searchParams.get(SOCIAL_AUTH_PROVIDER_PARAM);

  // When embedded, the page URL's social-auth error params belong to the page, not to
  // this instance — rendering them here would show a stale banner (and re-emit its GTM
  // event) every time the modal is opened.
  const isEmbedded = Boolean(onSuccess);

  const getSocialLoginErrorContent = (): ReactNode | null => {
    if (isGamesApp || isEmbedded || !isValidSocialError) {
      return null;
    }

    emitGTMEvent('USER_SOCIAL_SIGNUP_FAILED', {
      social_error: socialLoginError,
      social_provider,
    });

    if (socialLoginError === SOCIAL_LOGIN_ERRORS.ALREADY_REGISTERED_PLEASE_LOGIN) {
      return t.rich(`errors.socialLogin.${socialLoginError}`, {
        signIn: (chunks) => (
          <Button
            type='button'
            variant='noStyle'
            size='noStyle'
            className='text-secondary hover:text-secondary-hover! underline'
            onClick={handleCreateAccountClick}
          >
            {chunks}
          </Button>
        ),
      });
    }

    return t(`errors.socialLogin.${socialLoginError}`);
  };

  const displayError = errors.root?.message || getSocialLoginErrorContent();

  return (
    <div className='mx-auto flex w-full max-w-96 flex-col items-center'>
      {displayError && (
        <div role='alert'>
          <MessageBox type='error' className='mb-4 w-full' title={t('errors.messageBoxTitle')}>
            {displayError}
          </MessageBox>
        </div>
      )}
      {!hideSocialButtons && (
        <>
          <div className='mt-4 flex w-full max-w-96 flex-col gap-2'>
            <FacebookLink
              onStart={() => onSocialStart?.(AccountProvider.FACEBOOK)}
              intent='signup'
              buttonText={`${t('continueWith')} ${displayNameFacebook}`}
              callbackUrl={callbackUrl}
              onInClientSuccess={onInClientSuccess}
              returnTo={redirectUrl || undefined}
              className='min-h-11.5'
              subscribeToNewsletter={subscribeToNewsletter}
            />
            <GoogleLink
              onStart={() => onSocialStart?.(AccountProvider.GOOGLE)}
              intent='signup'
              buttonText={`${t('continueWith')} ${displayNameGoogle}`}
              callbackUrl={callbackUrl}
              onInClientSuccess={onInClientSuccess}
              returnTo={redirectUrl || undefined}
              className='min-h-11.5'
              subscribeToNewsletter={subscribeToNewsletter}
            />
          </div>
          <FormSeparator text={t('orSignUpWithEmail')} />
        </>
      )}
      <Form onSubmit={handleSubmit(onSubmit)} className='' aria-labelledby={titleId}>
        <div className='flex flex-col gap-2'>
          <InputField
            isRequired
            inputProps={register('email')}
            errorMessage={resolveErrorMessage(errors.email, t)}
            type='email'
            placeholder={t('email')}
            autoComplete='email'
          />
          <div className='flex flex-col'>
            <InputField
              isRequired
              inputProps={register('password')}
              type='password'
              placeholder={t('password')}
              autoComplete='new-password'
            />
            {showPasswordRequirementHints && (
              <ul className='mt-1 space-y-1 pl-1 text-sm'>
                {passwordRequirementItems.map((item) => {
                  const Icon = item.met ? CheckCircle2 : XCircle;
                  return (
                    <li key={item.key} className='flex items-start gap-2'>
                      <Icon
                        aria-hidden='true'
                        className={
                          item.met
                            ? 'text-success mt-0.5 h-5 w-5 shrink-0'
                            : 'text-error mt-0.5 h-5 w-5 shrink-0'
                        }
                      />
                      <span className={item.met ? 'text-success' : 'text-error'}>{item.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <div className='mt-4 flex items-center gap-2'>
          <Controller
            name='subscribeToNewsletter'
            control={control}
            render={({ field }) => (
              <>
                <Field className='subscribeToNewsletter'>
                  <Checkbox
                    id='subscribe'
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(!!checked)}
                  />
                </Field>
                <label htmlFor='subscribe' className='text-sm'>
                  {newsletterLabel ?? t('subscribeLabel')}
                </label>
              </>
            )}
          />
        </div>
        <FormSubmit aria-label={t('signUp')} disabled={isSubmitting} className='mx-auto my-8'>
          {isSubmitting ? t('creatingAccount') : t('signUp')}
        </FormSubmit>
      </Form>
      {showSignInLink && (
        <div className='mx-auto flex items-center gap-3'>
          {t('alreadyHaveAccount')}{' '}
          <Button
            type='button'
            variant='noStyle'
            size='noStyle'
            className='text-secondary hover:text-secondary-hover! underline'
            onClick={handleCreateAccountClick}
          >
            {t('signIn')}.
          </Button>
        </div>
      )}
    </div>
  );
};
